import React, { useEffect, useState } from 'react';
import Alert from '../../components/Alert';
import Button from '../../components/Button';
import Loader from '../../components/Loader';
import { useAxios } from '../../utils/hooks';
import c from 'classnames';
import { StripeMode } from '../../types/pricingPage';
import { Pill } from '../../components/Pill';

interface BubbleEventMeta {
    webhookEvent: string;
    webhookEventId: string;
    stripeMode: StripeMode;
    appName: string;
    email?: string;
}

interface BubbleEventErrorMeta {
    error: string;
}

interface BubbleEvent {
    id: string;
    event: string;
    createdAt: number;
    thingId: string;
    eventData: { email?: string };
    metadata: BubbleEventMeta | BubbleEventErrorMeta;
}

function isBubbleEventMeta(meta: BubbleEventMeta | BubbleEventErrorMeta): meta is BubbleEventMeta {
    return !!(meta as BubbleEventMeta).webhookEvent;
}

const PER_PAGE = 50;

export default function BubbleEventLogs() {
    const axios = useAxios();
    const [data, setData] = useState<BubbleEvent[] | string>();
    const [page, setPage] = useState(0);
    const [refreshKey, setRefeshKey] = useState(0);
    const [loading, setLoading] = useState(true);

    const handleLoadMore = () => {
        setPage(page + 1);
    };

    const handleRefresh = () => {
        setPage(0);
        setRefeshKey(refreshKey + 1);
    };

    useEffect(() => {
        if (!axios || !axios.current) {
            return;
        }
        setLoading(true);
        axios.current
            .get<BubbleEvent[]>(`/bubble/events?limit=${PER_PAGE}&skip=${page * PER_PAGE}`)
            .then((res) => {
                setData((data) => (page > 0 && Array.isArray(data) ? [...data, ...res.data] : res.data));
            })
            .catch((err) => {
                console.error(err);
                setData(err.message);
            })
            .finally(() => setLoading(false));
    }, [axios, page, refreshKey]);
    return (
        <div className="mx-auto mt-12" style={{ maxWidth: '71rem' }}>
            <p className="text-xl pb-3 flex items-center">
                <span>Bubble Event Log</span>
                <Button kind="outline" className="ml-2" state={loading ? 'SAVING' : 'READY'} onClick={handleRefresh}>
                    Refresh
                </Button>
            </p>
            <div className="bg-white overflow-auto relative rounded-lg shadow">
                <Table data={data} onLoadMore={handleLoadMore} />
            </div>
        </div>
    );
}

const FULL_SPAN = 4;

interface Props {
    data?: BubbleEvent[] | string;
    onLoadMore: () => void;
}
function Table({ data, onLoadMore }: Props) {
    const [expandedId, setExpandedId] = useState<string>();

    if (!data) {
        return <Loader full={false} />;
    } else if (typeof data === 'string') {
        return <Alert title="Error" body="Something went wrong please try again" />;
    }
    return (
        <>
            {typeof data === 'object' && (
                <table className="min-w-full bg-white flex flex-row flex-nowrap sm:table">
                    <thead className="bg-blue-800 text-white">
                        <tr className="flex flex-col flex-nowrap sm:table-row">
                            <th className="text-left py-3 px-4 uppercase font-semibold text-sm">Date</th>
                            <th className="text-left py-3 px-4 uppercase font-semibold text-sm">Event</th>
                            <th className="text-left py-3 px-4 uppercase font-semibold text-sm">Object</th>
                            <th className="w-1/3 text-left py-3 px-4 uppercase font-semibold text-sm">Data</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 && (
                            <tr>
                                <td className="text-center py-3 px-4 w-full" colSpan={FULL_SPAN}>
                                    No events found
                                </td>
                            </tr>
                        )}
                        {data.map((event) => (
                            <tr
                                key={event.id}
                                className="flex flex-col flex-nowrap sm:table-row border-b sm:border-none"
                            >
                                <td className="text-left py-3 px-4 text-sm">
                                    {new Date(event.createdAt).toLocaleString('en-US', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false,
                                    })}
                                </td>
                                <td className="text-left py-3 px-4">
                                    {isBubbleEventMeta(event.metadata) ? (
                                        event.event
                                    ) : (
                                        <>
                                            <Pill title="Error" colour="RED" />
                                            <span className="text-sm ml-2">{event.metadata.error}</span>
                                        </>
                                    )}
                                    {isBubbleEventMeta(event.metadata) && (
                                        <p className="text-sm font-light">
                                            <a
                                                href={`https://dashboard.stripe.com/${
                                                    event.metadata.stripeMode === StripeMode.TEST ? 'test/' : ''
                                                }events/${event.metadata.webhookEventId}`}
                                                className="link"
                                            >
                                                {event.metadata.webhookEvent}
                                            </a>
                                        </p>
                                    )}
                                </td>
                                <td className="text-left py-3 px-4">
                                    {event.thingId}
                                    {!!event.eventData.email && (
                                        <p className="text-sm font-light">{event.eventData.email}</p>
                                    )}
                                    {isBubbleEventMeta(event.metadata) && (
                                        <>
                                            {!!event.metadata.email && (
                                                <p className="text-sm font-light">{event.metadata.email}</p>
                                            )}
                                            <p className="text-sm font-light">{event.metadata.appName}</p>
                                        </>
                                    )}
                                </td>
                                <td className="text-left py-3 px-4">
                                    {event.eventData && Object.keys(event.eventData).length > 0 && (
                                        <>
                                            <pre
                                                className={c('text-sm', {
                                                    'h-12 overflow-hidden block': expandedId !== event.id,
                                                })}
                                            >
                                                {JSON.stringify(event.eventData, null, 2)}
                                            </pre>
                                            {expandedId !== event.id && (
                                                <div className="w-full text-left">
                                                    <button
                                                        onClick={() => setExpandedId(event.id)}
                                                        className="link text-sm"
                                                    >
                                                        Show more
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {data.length > 0 && (
                            <tr>
                                <td className="text-center py-3 px-4 w-full" colSpan={FULL_SPAN}>
                                    <Button kind="outline" onClick={onLoadMore}>
                                        Load more
                                    </Button>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}
        </>
    );
}
